import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { MapPin, Footprints, Heart, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SecondDateButton } from "@/components/SecondDate";

export function AgreedSpotBanner({ spot }) {
  if (!spot) return null;
  return (
    <a href={spot.map_url} target="_blank" rel="noopener noreferrer" data-testid="agreed-spot-banner"
      className="flex-shrink-0 mb-2 flex items-center gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-3.5 py-2.5 hover:bg-emerald-400/15 transition-colors">
      <MapPin className="h-4 w-4 text-emerald-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-emerald-300/80">Meeting first at</p>
        <p data-testid="agreed-spot-name" className="text-sm font-semibold truncate">{spot.name}</p>
      </div>
      <span className="text-[11px] text-emerald-100/70 flex items-center gap-1 shrink-0"><Footprints className="h-3 w-3" /> {spot.walk_min} min</span>
      <span data-testid="agreed-spot-map" className="text-xs font-semibold text-sky-300 shrink-0">Map →</span>
    </a>
  );
}

const ANSWERS = [["loved", "Loved it 💛"], ["nice", "It was nice"], ["not_for_me", "Not for me"], ["didnt_meet", "We didn't meet"]];

export function DateCheckinCard({ matchId, partnerName, onProposed }) {
  const [state, setState] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/matches/${matchId}/checkin`).then((r) => setState(r.data)).catch(() => {}); }, [matchId]);
  if (!state?.due) return null;

  if (state.answer) {
    return (
      <div data-testid="checkin-done" className="flex-shrink-0 mb-2 rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-xs text-emerald-100/70">
        <div className="flex items-center gap-2">
          <Heart className={`h-3.5 w-3.5 ${state.both_loved ? "text-amber-300 fill-amber-300" : "text-white/40"}`} />
          {state.both_loved ? <span data-testid="checkin-both-loved"><b className="text-amber-200">You both loved it!</b> Plan the next one with {partnerName}.</span> : "Thanks — your check-in is private."}
        </div>
        {state.both_loved && <SecondDateButton matchId={matchId} onProposed={onProposed} />}
      </div>
    );
  }

  const submit = async () => {
    setBusy(true);
    try { setState((await api.post(`/matches/${matchId}/checkin`, { answer, comment })).data); toast.success("Thanks for checking in"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't save"); }
    setBusy(false);
  };
  return (
    <div data-testid="checkin-card" className="flex-shrink-0 mb-2 glass rounded-2xl p-3.5 border border-sky-400/30">
      <p className="font-display font-bold text-sm">How did the date with {partnerName} go?</p>
      <p className="text-[11px] text-emerald-100/50">Private — {partnerName} only hears if you both loved it.</p>
      <div className="flex flex-wrap gap-2 mt-2.5">
        {ANSWERS.map(([k, label]) => (
          <button key={k} data-testid={`checkin-answer-${k}`} onClick={() => setAnswer(k)}
            className={`h-8 px-3 rounded-full text-xs border transition-colors ${answer === k ? "border-amber-400/70 bg-amber-400/15 text-amber-100" : "border-white/15 text-white/75 hover:bg-white/5"}`}>{label}</button>
        ))}
      </div>
      {answer && (
        <>
          <Textarea data-testid="checkin-comment" value={comment} onChange={(e) => setComment(e.target.value.slice(0, 500))}
            placeholder="Anything to add? (optional)" className="mt-2.5 bg-white/5 border-white/10 text-sm min-h-[60px]" />
          <button data-testid="checkin-submit" disabled={busy} onClick={submit}
            className="btn-gold rounded-full h-9 px-5 text-xs font-semibold mt-2 flex items-center gap-1.5">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Send check-in
          </button>
        </>
      )}
    </div>
  );
}
